import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Element } from '@/data/elements';

export function exportElementToPDF(element: Element) {
  const doc = new jsPDF();

  // En-tête avec symbole et nom
  doc.setFillColor(10, 25, 47); // #0a192f
  doc.rect(0, 0, 210, 40, 'F');

  doc.setTextColor(100, 255, 218); // #64ffda
  doc.setFontSize(48);
  doc.setFont('helvetica', 'bold');
  doc.text(element.symbol, 15, 30);

  doc.setFontSize(24);
  doc.setTextColor(255, 255, 255);
  doc.text(element.name, 50, 25);

  doc.setFontSize(12);
  doc.setTextColor(168, 178, 209); // #a8b2d1
  doc.text(`Numéro atomique: ${element.atomicNumber}`, 50, 33);

  // Catégorie badge
  doc.setFillColor(17, 34, 64); // #112240
  doc.roundedRect(140, 10, 60, 8, 2, 2, 'F');
  doc.setTextColor(100, 255, 218);
  doc.setFontSize(10);
  const categoryNames: Record<string, string> = {
    'alkali_metal': 'Métal alcalin',
    'alkaline_earth_metal': 'Métal alcalino-terreux',
    'transition_metal': 'Métal de transition',
    'post_transition_metal': 'Métal post-transition',
    'metalloid': 'Métalloïde',
    'nonmetal': 'Non-métal',
    'halogen': 'Halogène',
    'noble_gas': 'Gaz noble',
    'lanthanide': 'Lanthanide',
    'actinide': 'Actinide',
  };
  doc.text(categoryNames[element.category] || element.category, 170, 16, { align: 'center' });

  // Propriétés physiques
  let yPos = 50;
  doc.setTextColor(100, 255, 218);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Propriétés Physiques', 15, yPos);

  yPos += 10;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const properties = [
    ['Masse atomique', `${element.atomicWeight.toFixed(5)} u`],
    ['Densité', element.density ? `${element.density} g/cm³` : 'N/A'],
    ['Point de fusion', element.meltingPoint ? `${element.meltingPoint} K` : 'N/A'],
    ['Point d\'ébullition', element.boilingPoint ? `${element.boilingPoint} K` : 'N/A'],
    ['Électronégativité', element.electronegativity ? element.electronegativity.toString() : 'N/A'],
    ['État', element.state === 'solid' ? 'Solide' : element.state === 'liquid' ? 'Liquide' : 'Gaz'],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Propriété', 'Valeur']],
    body: properties,
    theme: 'grid',
    headStyles: {
      fillColor: [100, 255, 218],
      textColor: [10, 25, 47],
      fontSize: 10,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { left: 15, right: 15 },
  });

  // Configuration électronique
  yPos = (doc as any).lastAutoTable.finalY + 10;
  doc.setTextColor(100, 255, 218);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Configuration Électronique', 15, yPos);

  yPos += 7;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.setFillColor(240, 240, 240);
  doc.roundedRect(15, yPos - 5, 180, 10, 2, 2, 'F');
  doc.text(element.electronConfiguration, 20, yPos + 2);

  // Description
  yPos += 15;
  doc.setTextColor(100, 255, 218);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Description', 15, yPos);

  yPos += 7;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const descriptionLines = doc.splitTextToSize(element.description, 180);
  doc.text(descriptionLines, 15, yPos);

  // Utilisations
  yPos += descriptionLines.length * 5 + 10;
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }

  doc.setTextColor(100, 255, 218);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Utilisations', 15, yPos);

  yPos += 7;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const usesLines = doc.splitTextToSize(element.uses, 180);
  doc.text(usesLines, 15, yPos);

  // Découverte
  if (element.discoveredBy) {
    yPos += usesLines.length * 5 + 10;
    if (yPos > 260) {
      doc.addPage();
      yPos = 20;
    }

    doc.setTextColor(100, 255, 218);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Découverte', 15, yPos);

    yPos += 7;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Découvert par: ${element.discoveredBy}`, 15, yPos);
    if (element.yearDiscovered) {
      yPos += 6;
      doc.text(`Année: ${element.yearDiscovered}`, 15, yPos);
    }
  }

  // Isotopes (si présents)
  if (element.isotopes && element.isotopes.length > 0) {
    yPos += 15;
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setTextColor(100, 255, 218);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Isotopes (${element.isotopes.length})`, 15, yPos);

    yPos += 5;

    const isotopesData = element.isotopes.slice(0, 15).map(isotope => [
      isotope.symbol,
      isotope.massNumber.toString(),
      isotope.halfLife,
      isotope.isStable ? 'Stable' : 'Radioactif',
      isotope.decayMode.join(', '),
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Symbole', 'Masse', 'Demi-vie', 'État', 'Rayonnement']],
      body: isotopesData,
      theme: 'grid',
      headStyles: {
        fillColor: [100, 255, 218],
        textColor: [10, 25, 47],
        fontSize: 9,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 20 },
        2: { cellWidth: 35 },
        3: { cellWidth: 25 },
        4: { cellWidth: 'auto' },
      },
      margin: { left: 15, right: 15 },
    });

    if (element.isotopes.length > 15) {
      const finalY = (doc as any).lastAutoTable.finalY + 5;
      doc.setTextColor(168, 178, 209);
      doc.setFontSize(8);
      doc.text(`... et ${element.isotopes.length - 15} autres isotopes`, 15, finalY);
    }
  }

  // Pied de page sur chaque page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setTextColor(168, 178, 209);
    doc.setFontSize(8);
    doc.text(
      'Généré par Elementium - Tableau Périodique Interactif',
      105,
      285,
      { align: 'center' }
    );
    doc.text(`Page ${i} sur ${pageCount}`, 105, 290, { align: 'center' });
  }

  // Télécharger le PDF
  doc.save(`${element.name}_${element.symbol}_Elementium.pdf`);
}

export function exportAllElementsToPDF(elements: Element[]) {
  const doc = new jsPDF();

  // Page de titre
  doc.setFillColor(10, 25, 47);
  doc.rect(0, 0, 210, 297, 'F');

  doc.setTextColor(100, 255, 218);
  doc.setFontSize(36);
  doc.setFont('helvetica', 'bold');
  doc.text('Tableau Périodique', 105, 100, { align: 'center' });
  doc.text('des Éléments', 105, 120, { align: 'center' });

  doc.setFontSize(16);
  doc.setTextColor(168, 178, 209);
  doc.text('Les 118 Éléments Chimiques', 105, 140, { align: 'center' });

  doc.setFontSize(12);
  doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 105, 200, { align: 'center' });

  doc.setFontSize(10);
  doc.text('Elementium - Tableau Périodique Interactif', 105, 270, { align: 'center' });

  // Table de tous les éléments
  doc.addPage();

  doc.setTextColor(100, 255, 218);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Liste Complète des Éléments', 15, 20);

  const elementsData = elements.map(el => [
    el.atomicNumber.toString(),
    el.symbol,
    el.name,
    el.atomicWeight.toFixed(3),
    categoryNames[el.category] || el.category,
  ]);

  const categoryNames: Record<string, string> = {
    'alkali_metal': 'Métal alcalin',
    'alkaline_earth_metal': 'Métal alcalino-terreux',
    'transition_metal': 'Métal de transition',
    'post_transition_metal': 'Métal post-transition',
    'metalloid': 'Métalloïde',
    'nonmetal': 'Non-métal',
    'halogen': 'Halogène',
    'noble_gas': 'Gaz noble',
    'lanthanide': 'Lanthanide',
    'actinide': 'Actinide',
  };

  autoTable(doc, {
    startY: 30,
    head: [['N°', 'Symbole', 'Nom', 'Masse (u)', 'Catégorie']],
    body: elementsData,
    theme: 'grid',
    headStyles: {
      fillColor: [100, 255, 218],
      textColor: [10, 25, 47],
      fontSize: 10,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 20 },
      2: { cellWidth: 50 },
      3: { cellWidth: 25 },
      4: { cellWidth: 'auto' },
    },
    margin: { left: 15, right: 15 },
  });

  // Pied de page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setTextColor(168, 178, 209);
    doc.setFontSize(8);
    doc.text(
      'Généré par Elementium - Tableau Périodique Interactif',
      105,
      285,
      { align: 'center' }
    );
    doc.text(`Page ${i} sur ${pageCount}`, 105, 290, { align: 'center' });
  }

  doc.save(`Tableau_Periodique_Complet_Elementium.pdf`);
}
